import { createRouter, createWebHistory } from 'vue-router';
import { useSessionStorage } from '@vueuse/core';
import { pickBy, merge } from 'lodash-es';
import { Userpilot } from 'userpilot';

import {
  BASE_DOMAIN,
  NODE_ENV,
} from '@/config/appConfig';

import useUserStore from '@/stores/user';
import usePermissionsStore from '@/stores/permissions';

import BaseLoggedInView from '@/views/BaseLoggedInView.vue';

const sessionStore = useSessionStorage('tagboard-app', {});

// Store reference to app instance for accessing $ld
let appInstance = null;
export const setAppInstance = (app) => {
  appInstance = app;
};

let launchDarklyReadyPromise = null;

function waitForLaunchDarklyReady(timeoutMs = 1500) {
  const ldClient = appInstance?.config?.globalProperties?.$ld;

  if (!ldClient) {
    return Promise.resolve(null);
  }

  if (!launchDarklyReadyPromise) {
    launchDarklyReadyPromise = new Promise((resolve) => {
      if (ldClient.ready) {
        resolve(ldClient);
        return;
      }

      ldClient.onReady(() => {
        resolve(ldClient);
      });
    });
  }

  if (ldClient.ready) {
    return Promise.resolve(ldClient);
  }

  return Promise.race([
    launchDarklyReadyPromise,
    new Promise((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, timeoutMs);
    }),
  ]);
}

async function getLaunchDarklyFlagVariation(flagKey, defaultValue = false) {
  const ldClient = await waitForLaunchDarklyReady();

  if (!ldClient) {
    return defaultValue;
  }

  try {
    return ldClient.client.variation(flagKey, defaultValue);
  } catch (error) {
    console.warn('[Router] Failed to evaluate LaunchDarkly flag.', error); // eslint-disable-line no-console
    return defaultValue;
  }
}

function makeLaunchDarklyBeforeEnter({
  flagKey,
  defaultValue = false,
  redirectName = 'dashboard',
}) {
  return async () => {
    const isEnabled = await getLaunchDarklyFlagVariation(flagKey, defaultValue);

    if (!isEnabled) {
      return { name: redirectName };
    }

    return true;
  };
}

async function shouldIdentifyUserpilot() {
  if (import.meta.env.DEV) {
    return true;
  }

  if (NODE_ENV !== 'stage' && NODE_ENV !== 'development') {
    return true;
  }

  if (NODE_ENV !== 'stage') {
    return false;
  }

  const ldClient = await waitForLaunchDarklyReady();

  if (!ldClient) {
    return false;
  }

  try {
    // Enable this only for dev staff in order to exclude QA generated accounts to reduce counts against UserPilot MAUs.
    return ldClient.client.variation('enable-userpilot-in-stage', false);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      '[Userpilot] Failed to evaluate staging LaunchDarkly flag; skipping identify.',
      error,
    );

    return false;
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'base',
      component: BaseLoggedInView,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: '',
          name: 'home',
          component: () => import('@/views/HomeView.vue'),
          children: [
            {
              path: '',
              name: 'dashboard',
              component: () => import('@/views/DashboardView.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Creator Dashboard',
              },
            },
            {
              path: 'welcome',
              name: 'welcome',
              component: () => import('@/views/WelcomeView.vue'),
              // eslint-disable-next-line consistent-return
              beforeEnter: () => {
                // don't show upsells to users with a team or plan
                if (!useUserStore().isFreeAccountNoTeam) {
                  return { name: 'dashboard' };
                }
              },
            },
            {
              path: 'curated-content',
              name: 'curated-content',
              redirect: {
                name: 'boards',
              },
            },
            {
              // Custom, Polls, and Social all render through this one route, switching
              // on hash so the secondary nav doesn't remount when tabs change. Polls'
              // internal Classic Polls/Displays sub-tabs (temporary, being deprecated)
              // use their own distinct hash values so they don't collide with this one.
              path: 'graphics',
              name: 'graphics',
              component: () => import('@/views/GraphicsHubView.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Custom',
                preserveQuery: {
                  useHash: true,
                  params: ['tags'],
                },
              },
            },
            {
              path: 'polls',
              name: 'polls',
              redirect: (to) => {
                // Old bookmarks/links used #polls for Classic Polls and #displays
                // for Classic Displays (this route's own tabs, before Polls moved
                // under the Graphics hub). Map those forward to the renamed hashes.
                const LEGACY_HASHES = {
                  '#polls': '#classicpolls',
                  '#displays': '#classicdisplays',
                };

                return {
                  name: 'graphics',
                  hash: LEGACY_HASHES[to.hash] || to.hash || '#polls',
                  query: to.query,
                };
              },
            },
            {
              path: 'social',
              name: 'social',
              redirect: (to) => (to.hash === '#boards'
                ? { name: 'boards', hash: '', query: to.query }
                : { name: 'graphics', hash: '#social', query: to.query }),
            },
            {
              // Use /graphic/create for a new graphic
              path: 'graphic/:graphicId',
              name: 'graphic-editor',
              component: () => import('@/views/GraphicEditorView.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Graphic Editor',
              },
            },
            {
              // Shared shell so the secondary nav doesn't remount when switching
              // between Boards and Shoppable.
              path: '',
              name: 'curated-content-hub',
              component: () => import('@/views/CuratedContentHubView.vue'),
              meta: {
                teamOrPlanRequired: true,
              },
              children: [
                {
                  path: 'boards',
                  name: 'boards',
                  component: () => import('@/views/social/BoardsView.vue'),
                  meta: {
                    title: 'Boards',
                    preserveQuery: {
                      params: ['tags'],
                    },
                  },
                },
                {
                  path: 'shoppable',
                  name: 'shoppable',
                  component: () => import('@/views/ShoppableView.vue'),
                  redirect: {
                    name: 'browse-products',
                  },
                  meta: {
                    title: 'Shoppable',
                  },
                  children: [
                    {
                      path: 'browse',
                      name: 'browse-products',
                      component: () => import('@/views/BrowseProductsView.vue'),
                    },
                    {
                      path: 'saved/:collectionId?',
                      name: 'saved-products',
                      component: () => import('@/views/SavedProductsView.vue'),
                    },
                  ],
                },
              ],
            },
            {
              // Use /display/create/:layout for a new display
              path: 'display/:displayId/:layout?',
              name: 'display-editor',
              component: () => import('@/views/DisplayEditorView.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Display Editor',
              },
            },
            {
              path: 'board/:boardId/:tab?',
              name: 'board-editor',
              component: () => import('@/views/BoardEditorView.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Board Editor',
              },
            },
            {
              path: 'interactive',
              name: 'interactive',
              component: () => import('@/views/InteractiveView.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Interactive',
                preserveQuery: {
                  useHash: true,
                  params: ['tags'],
                },
              },
              beforeEnter: async () => {
                return { name: 'polls' }; // Redirect to /polls
              },
            },
            {
              // Use /panel/create/:type for a new panel
              path: 'panel/:panelId/:type?',
              name: 'panel-editor',
              component: () => import('@/views/PanelEditorView.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Panel Editor',
              },
            },
            {
              // Use /embed/create/:type for a new embed
              path: 'embed/:embedId/:type?',
              name: 'embed-editor',
              component: () => import('@/views/EmbedEditorView.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Embed Editor',
              },
            },
            {
              path: 'settings',
              name: 'settings',
              component: () => import('@/views/AccountSettingsView.vue'),
              children: [
                {
                  alias: ['/settings'],
                  path: 'profile',
                  name: 'profile',
                  component: () => import('@/views/settings/ProfileView.vue'),
                },
                {
                  path: 'integrations',
                  name: 'integrations',
                  component: () => import('@/views/settings/IntegrationsView.vue'),
                },
                {
                  path: 'integrations/facebook',
                  name: 'integrations/facebook',
                  component: () => import('@/views/settings/FacebookSettingsView.vue'),
                },
                {
                  path: 'integrations/threads',
                  name: 'integrations/threads',
                  component: () => import('@/views/settings/ThreadsSettingsView.vue'),
                },
                {
                  path: 'integrations/streamdeck',
                  name: 'integrations/streamdeck',
                  component: () => import('@/views/settings/streamdeck/StreamDeckSettingsView.vue'),
                  meta: {
                    teamOrPlanRequired: true,
                  },
                },
                {
                  path: 'integrations/sportradar',
                  name: 'integrations/sportradar',
                  component: () => import('@/views/settings/sportradar/SportradarSettingsView.vue'),
                  meta: {
                    teamOrPlanRequired: true,
                  },
                  // eslint-disable-next-line consistent-return
                  beforeEnter: () => {
                    // Requires at least one sportradar element in the permission check
                    if (!usePermissionsStore().hasSportradar) {
                      return { name: 'integrations' };
                    }
                  },
                },
                {
                  path: 'team',
                  name: 'team',
                  component: () => import('@/views/settings/TeamView.vue'),
                  meta: {
                    teamOrPlanRequired: true,
                  },
                },
                {
                  path: 'devtools',
                  name: 'devtools',
                  component: () => import('@/views/settings/DeveloperToolsView.vue'),
                  meta: {
                    teamOrPlanRequired: true,
                  },
                },
                {
                  path: 'tags',
                  name: 'tags',
                  component: () => import('@/views/TagsView.vue'),
                  meta: {
                    teamOrPlanRequired: true,
                    title: 'Tags',
                  },
                },
              ],
              meta: {
                title: 'Settings',
              },
            },
            {
              path: 'analytics',
              name: 'analytics',
              component: () => import('@/views/AnalyticsView.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Analytics',
              },
            },
            {
              path: 'analytics/url-tracking-dashboard/:id',
              name: 'url-tracking-dashboard',
              component: () => import('@/views/analytics/TrackingUrlDashboard.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'QR Code Analytics Dashboard',
                section: 'analytics', // fpr getting the nav item active
              },
            },
            {
              path: 'analytics/tracking-link/:id',
              name: 'tracking-link-analytics',
              component: () => import('@/views/analytics/TrackingLinkDashboard.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Tracking Link Analytics',
                section: 'analytics',
              },
            },
            {
              path: 'analytics/poll/:id',
              name: 'poll-analytics',
              component: () => import('@/views/analytics/PollDashboard.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Poll Analytics',
                section: 'analytics',
              },
            },
            {
              path: 'analytics/embed-analytics-dashboard/:id',
              name: 'embed-analytics-dashboard',
              component: () => import('@/views/analytics/EmbedAnalyticsView.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Embed Analytics Dashboard',
                section: 'analytics', // fpr getting the nav item active
              },
            },
            {
              path: 'design-library',
              name: 'design-library',
              component: () => import('@/views/DesignLibraryView.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Media',
              },
            },
            {
              path: 'design-library/directory/:directoryId',
              name: 'design-library-directory',
              component: () => import('@/views/DesignLibraryView.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Media Directory',
              },
            },
            {
              path: 'templates',
              name: 'templates',
              component: () => import('@/views/TemplatesView.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Templates',
              },
            },
            {
              path: 'snippets',
              name: 'snippets',
              component: () => import('@/views/SnippetsView.vue'),
              meta: {
                staffOnly: true,
                title: 'Snippets',
              },
            },
            {
              path: 'snippet/:snippetId',
              name: 'snippet-editor',
              component: () => import('@/views/SnippetEditorView.vue'),
              meta: {
                staffOnly: true,
                title: 'Snippet Editor',
              },
            },
            {
              path: 'pages',
              name: 'pages-list',
              component: () => import('@/views/PagesHubView.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Pages',
              },
            },
            {
              path: 'shows',
              name: 'shows-list',
              component: () => import('@/views/ShowsListView.vue'),
              beforeEnter: makeLaunchDarklyBeforeEnter({
                flagKey: 'show-show-builder',
                defaultValue: false,
                redirectName: 'dashboard',
              }),
              meta: {
                teamOrPlanRequired: true,
                title: 'Shows',
              },
            },
            {
              path: 'search',
              name: 'search',
              component: () => import('@/views/SearchView.vue'),
              meta: {
                teamOrPlanRequired: true,
                title: 'Search',
              },
            },
          ],
        },
        {
          path: '/page/:pageId',
          name: 'page-editor',
          component: () => import('@/views/PageEditorView.vue'),
          meta: {
            title: 'Page Editor',
          },
        },
        {
          path: '/show/:showId',
          name: 'show-editor',
          component: () => import('@/views/ShowEditorView.vue'),
          beforeEnter: makeLaunchDarklyBeforeEnter({
            flagKey: 'show-show-builder',
            defaultValue: false,
            redirectName: 'dashboard',
          }),
          meta: {
            teamOrPlanRequired: true,
            title: 'Show Editor',
          },
        },
        {
          path: '/poll/:pollId',
          component: () => import('@/views/polls/PollView.vue'),
          meta: {
            title: 'Poll Editor',
          },
          children: [
            {
              path: '',
              name: 'poll-editor',
              component: () => import('@/views/polls/PollEditor.vue'),
            },
            {
              path: 'share',
              name: 'poll-share',
              component: () => import('@/views/polls/PollShare.vue'),
            },
            {
              path: 'displays',
              name: 'poll-displays',
              component: () => import('@/views/polls/PollDisplays.vue'),
            },
            {
              path: 'stats',
              name: 'poll-stats',
              component: () => import('@/views/polls/PollStats.vue'),
            },
          ],
        },
      ],
    },
    {
      path: '/select-asset/:directoryId?',
      name: 'select-asset',
      component: () => import('@/views/SelectAsset.vue'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/signin',
      name: 'signin',
      beforeEnter: (to) => {
        // eslint-disable-next-line max-len, vue/max-len
        window.location.href = `https://user.${BASE_DOMAIN}/auth/auth0?returnUrl=${window.location.origin}${to.query.redirect || '/'}`;
      },
    },
    {
      path: '/email-change/:emailKey',
      name: 'email-change',
      component: () => import('@/views/EmailChange.vue'),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/invite/:key',
      name: 'invite',
      component: () => import('@/views/InviteProcessingView.vue'),
      meta: {
        requiresAuth: false,
      },
    },
    {
      path: '/invite/:key/confirm',
      name: 'invite-signin',
      component: () => import('@/views/InviteConfirmView.vue'),
    },
    {
      path: '/invite-expired',
      name: 'invite-expired',
      meta: {
        requiresAuth: false, // Explicitly state no auth is required
      },
      component: () => import('@/views/InviteExpiredView.vue'),
    },
    {
      path: '/logout',
      name: 'logout',
      beforeEnter: () => {
        useUserStore().logout();
      },
    },
    {
      path: '/:pathMatch(.*)*',
      name: '404',
      component: () => import('@/views/NotFound.vue'),
    },
  ],
});

// eslint-disable-next-line consistent-return
router.beforeEach(async (to, from) => {
  const userStore = useUserStore();

  if (to.meta.requiresAuth) {
    // Load the user if not yet authenticated, redirecting to sign in on failure.
    if (!userStore.isAuthenticated) {
      try {
        await userStore.loadUser();
      } catch (error) {
        console.error('Failed to load user:', error); // eslint-disable-line no-console
        return { name: 'signin', query: { redirect: to.fullPath } };
      }
    }

    // Identify the user in Userpilot on every auth navigation (first load and refresh).
    // Userpilot deduplicates calls internally so this is safe to call on every route change.
    if (await shouldIdentifyUserpilot()) {
      try {
        Userpilot.identify(userStore.userId, {
          email: userStore.user?.profile?.email,
          name: userStore.user?.profile?.displayName,
        });
      } catch (error) {
        console.error('Failed to identify user in Userpilot:', error); // eslint-disable-line no-console
      }
    }
  }

  if (to.meta.staffOnly && !userStore.isStaff) {
    // Send user to where they were if they are not staff
    return from;
  }

  if (to.meta.teamOrPlanRequired && userStore.isFreeAccountNoTeam) {
    // Send user to welcome upsell if they don't have a team or plan
    return { name: 'welcome' };
  }

  // preserve hideNavigation query param (like showing content view in a modal via iframe)
  if (from.query.hideNavigation) {
    // eslint-disable-next-line no-param-reassign
    to.query = { ...to.query, hideNavigation: true };
  }

  // restore query params from session storage
  if (to.meta.preserveQuery) {
    const { useHash } = to.meta.preserveQuery;
    const storageKey = useHash ? `${to.name}-${to.hash}` : to.name;
    // only restore query params if the destination route has none
    // this prevents query params from being overwritten when navigating from a saved/shared link
    if (!Object.keys(to.query).length && sessionStore.value[storageKey]?.query) {
      return { ...to, query: sessionStore.value[storageKey]?.query };
    }
  }
});

router.afterEach((to, from) => {
  Userpilot.reload();

  const DEFAULT_TITLE = 'Tagboard';
  document.title = to.meta.title || DEFAULT_TITLE;
  // save selected query params to session storage
  if (from.meta.preserveQuery) {
    let queryParams = from.query;

    // still on same page, get the updated query params
    if (to.name === from.name && to.hash === from.hash) {
      queryParams = to.query;
    }

    const { useHash, params } = from.meta.preserveQuery;
    const storageKey = useHash ? `${from.name}-${from.hash}` : from.name;

    const picked = pickBy(queryParams, (value, key) => {
      if (params.includes(key)) {
        if (Array.isArray(value)) {
          return value.length;
        }

        return true;
      }

      return false;
    });

    if (!Object.keys(picked).length) {
      if ('query' in (sessionStore.value?.[storageKey] || {})) {
        delete sessionStore.value[storageKey].query;
      }
    } else {
      sessionStore.value = merge(sessionStore.value, { [storageKey]: { query: picked } });
    }
  }
});

export default router;
